import type { FC, ReactNode } from 'react'
import type { PageContent } from '@/types/content'

import { cms } from '@/lib/cms'
import { cn } from '@/lib/utils'
import { Picture } from '@/components/media/picture'
import { MoonPhase } from '@/components/media/moon-phase'

interface PageHeroProps {
    page: PageContent | null
    fallback: {
        eyebrow: string
        heading: string
        intro?: string
    }
    image?: string | null
    imageAlt?: string | null
    phase?: number
    tone?: 'dark' | 'paper'
    children?: ReactNode
    className?: string
}

/** The opening band of an index page. Its words come from the pages collection so they stay editable. */
export const PageHero: FC<PageHeroProps> = ({ page, fallback, image, imageAlt, phase = 0.62, tone = 'dark', children, className }): ReactNode => {
    const eyebrow = page?.eyebrow ?? null
    const heading = page?.heading ?? null
    const intro = page?.intro ?? null

    return (
        <section className={cn('relative overflow-hidden pt-32 pb-16 sm:pt-40 sm:pb-20', tone === 'paper' && 'theme-paper', className)}>
            {image && tone === 'dark' && (
                <div className='absolute inset-0' aria-hidden='true'>
                    <Picture src={image} alt={imageAlt ?? ''} priority grade='night' className='opacity-70' />
                    <div className='absolute inset-0 bg-gradient-to-b from-obsidian/80 via-obsidian/55 to-obsidian' />
                    <div className='absolute inset-0 vignette' />
                </div>
            )}

            <div className='pointer-events-none absolute -right-24 top-20 hidden opacity-60 md:block' aria-hidden='true'>
                <MoonPhase phase={phase} size={340} color={tone === 'paper' ? '#141210' : '#E9E4DA'} shadow={tone === 'paper' ? 'rgba(20,18,16,0.04)' : 'rgba(233,228,218,0.025)'} className='opacity-[0.12]' />
            </div>

            <div className='shell relative'>
                <p className={cn('eyebrow mb-5 animate-in fade-in slide-in-from-bottom-2 duration-700', tone === 'paper' ? 'text-burgundy' : 'text-gold')}>
                    {eyebrow
                        ? <span {...cms('pages', page?.id, 'eyebrow')}>{eyebrow}</span>
                        : fallback.eyebrow}
                </p>

                <h1 className='max-w-4xl font-heading text-5xl font-light leading-[0.98] tracking-[-0.015em] animate-in fade-in slide-in-from-bottom-3 duration-1000 sm:text-6xl lg:text-7xl'>
                    {heading
                        ? <span {...cms('pages', page?.id, 'heading')}>{heading}</span>
                        : fallback.heading}
                </h1>

                {(intro || fallback.intro) && (
                    <p className='mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground animate-in fade-in duration-1000 sm:text-lg'>
                        {intro
                            ? <span {...cms('pages', page?.id, 'intro')}>{intro}</span>
                            : fallback.intro}
                    </p>
                )}

                {children && <div className='mt-10'>{children}</div>}
            </div>
        </section>
    )
}
